import { smartSearch } from "../lib/utils";
import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { invoicesAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Search, FileText, CheckCircle2, Clock, Trash2, Eye, DollarSign } from "lucide-react";
import { toast } from "sonner";

function InvoiceRow({ invoice, onView, onPay, onDelete }) {
  return (
    <tr data-testid={"invoice-row-" + invoice.id}>
      <td className="font-mono text-sm text-primary">{invoice.invoice_number}</td>
      <td>
        <span className={invoice.is_quote ? 'px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400' : 'px-2 py-1 rounded text-xs bg-primary/20 text-primary'}>
          {invoice.is_quote ? "Devis" : "Facture"}
        </span>
      </td>
      <td>{invoice.client_name}</td>
      <td className="font-medium">{invoice.total.toFixed(2)}€</td>
      <td>
        {invoice.is_quote ? (
          <span className="text-muted-foreground">-</span>
        ) : invoice.is_paid ? (
          <span className="flex items-center gap-1 text-accent">
            <CheckCircle2 className="w-4 h-4" />
            Payée
          </span>
        ) : (
          <span className="flex items-center gap-1 text-amber-500">
            <Clock className="w-4 h-4" />
            En attente
          </span>
        )}
      </td>
      <td className="text-muted-foreground text-sm">
        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
      </td>
      <td>
        <div className="flex gap-1">
          <button
            onClick={() => onView(invoice)}
            className="p-2 hover:bg-secondary rounded-md transition-colors duration-150"
            data-testid={"view-invoice-" + invoice.id}
          >
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          {!invoice.is_quote && !invoice.is_paid && (
            <button
              onClick={() => onPay(invoice.id)}
              className="p-2 hover:bg-accent/10 rounded-md transition-colors duration-150"
              data-testid={"pay-invoice-" + invoice.id}
            >
              <DollarSign className="w-4 h-4 text-accent" />
            </button>
          )}
          <button
            onClick={() => onDelete(invoice.id)}
            className="p-2 hover:bg-destructive/10 rounded-md transition-colors duration-150"
            data-testid={"delete-invoice-" + invoice.id}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function InvoiceItem({ item }) {
  return (
    <div className="flex justify-between text-sm bg-secondary/30 p-2 rounded">
      <span>{item.description}</span>
      <span>{item.quantity} x {item.unit_price.toFixed(2)}€</span>
    </div>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await invoicesAPI.list();
      setInvoices(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await invoicesAPI.markPaid(id);
      toast.success("Facture marquée comme payée");
      fetchInvoices();
      if (selectedInvoice && selectedInvoice.id === id) {
        setSelectedInvoice({ ...selectedInvoice, is_paid: true });
      }
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await invoicesAPI.delete(id);
      toast.success("Supprimé");
      fetchInvoices();
      setDetailDialogOpen(false);
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
  };

  // Calculate stats
  let totalRevenue = 0;
  let pendingAmount = 0;
  let quotesCount = 0;
  let filteredInvoices = [];

  for (let i = 0; i < invoices.length; i++) {
    const inv = invoices[i];
    if (!inv.is_quote && inv.is_paid) totalRevenue += inv.total;
    if (!inv.is_quote && !inv.is_paid) pendingAmount += inv.total;
    if (inv.is_quote) quotesCount++;

    // Filter
    const matchSearch = smartSearch(searchQuery, [inv.invoice_number, inv.client_name, inv.repair_id]);
    
    let matchType = typeFilter === "all";
    if (typeFilter === "quote") matchType = inv.is_quote;
    if (typeFilter === "invoice") matchType = !inv.is_quote;
    
    let matchStatus = statusFilter === "all";
    if (statusFilter === "paid") matchStatus = inv.is_paid;
    if (statusFilter === "unpaid") matchStatus = !inv.is_paid;

    if (matchSearch && matchType && matchStatus) {
      filteredInvoices.push(inv);
    }
  }

  const items = selectedInvoice ? selectedInvoice.items : [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="invoices-page">
        <div>
          <h1 className="font-chivo text-3xl font-bold text-foreground">Factures & Devis</h1>
          <p className="text-muted-foreground mt-1">{invoices.length} document(s)</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total encaissé</p>
              <p className="text-2xl font-light font-chivo text-accent mt-1">{totalRevenue.toFixed(2)}€</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">En attente</p>
              <p className="text-2xl font-light font-chivo text-amber-500 mt-1">{pendingAmount.toFixed(2)}€</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Devis</p>
              <p className="text-2xl font-light font-chivo text-foreground mt-1">{quotesCount}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="invoice-search-input"
              className="pl-10 bg-secondary/50"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-secondary/50">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="invoice">Factures</SelectItem>
              <SelectItem value="quote">Devis</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-secondary/50">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="paid">Payées</SelectItem>
              <SelectItem value="unpaid">Impayées</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune facture trouvée</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Type</th>
                  <th>Client</th>
                  <th>Total</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((inv) => (
                  <InvoiceRow key={inv.id} invoice={inv} onView={viewInvoice} onPay={handleMarkPaid} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {selectedInvoice ? (selectedInvoice.is_quote ? "Devis " : "Facture ") + selectedInvoice.invoice_number : ""}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4 mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Client</span>
                  <span>{selectedInvoice.client_name}</span>
                </div>
                
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium mb-2">Articles</p>
                  <div className="space-y-2">
                    {items.map((item, idx) => <InvoiceItem key={idx} item={item} />)}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between font-medium text-lg">
                    <span>Total</span>
                    <span className="text-primary">{selectedInvoice.total.toFixed(2)}€</span>
                  </div>
                </div>

                {!selectedInvoice.is_quote && !selectedInvoice.is_paid && (
                  <Button 
                    onClick={() => handleMarkPaid(selectedInvoice.id)}
                    className="w-full bg-accent hover:bg-accent/90"
                  >
                    Marquer comme payée
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
